import { createAction, props } from '@ngrx/store';
import { Map } from 'immutable';

import { DxFilterField, DxSortField } from '../../core/dx-types';
import { SubscriptionDatablockItemEntity } from '../components/subscription-configuration-stepper/subscription-datablock-item.entity';
import { SubscriptionProjectItemEntity } from '../components/subscription-configuration-stepper/subscription-project-item.entity';
import { SubscriptionItemEntity } from '../components/subscription-list/subscription-item.entity';
import { SimulationResultEntity } from '../components/subscription-simulator/simulation-result.entity';
import { ConsumedProjectItemEntity } from '../components/subscription-view/consumed-project-item.entity';
import { ProjectItemEntity } from '../components/subscription-view/consumer-config-modal/project-item.entity';
import { HistoryItemEntity } from '../components/subscription-view/history-list-modal/history-item.entity';
import { MemberAccessEntity } from '../components/subscription-view/permissions-config-modal/member-access.entity';
import { PublishedSubscriptionEntity } from '../components/subscription-view/published-subscription.entity';
import { ViewDetailsEntity } from '../components/subscription-view/view-details.entity';
import { SwitchableVersionProjectItemEntity } from '../components/version-switcher-modal/switchable-version-project-item.entity';
import { ColumnConfigItemEntity } from '../domain/column-config-item.entity';
import { ColumnModelItemEntity } from '../domain/column-model-item.entity';
import { FilterModelItemEntity } from '../domain/filter-model-item.entity';
import { InternalMetadataEntity } from '../domain/internal-metadata.entity';
import { ProfileItemEntity } from '../domain/profile-item.entity';
import { PublicMetadataEntity } from '../domain/public-metadata.entity';
import {
	DatablockColumn,
	FilterVersion,
	OperationType,
	SimulationDataSource,
	SimulationSortColumnType,
	SubscriptionAction,
	SubscriptionStatus,
} from '../domain/subscription.types';

export enum SubscriptionActions {
	InitSubscriptions = '[Subscription] Init subscriptions',
	GetSubscriptions = '[Subscription] Get subscriptions',
	FetchSubscriptionsSuccess = '[BE][Subscription] Fetch subscriptions success',
	RefreshSubscriptions = '[Subscription] Refresh subscription after ws',
	RefreshSubscriptionsSuccess = '[BE][Subscription] Refresh subscriptions success',
	FetchSubscriptionsFail = '[BE][Subscription] Fetch subscriptions fail',
	HasSubscriptionCreatePermissionSuccess = '[BE][Subscription] has subscription create permission success',
	HasSubscriptionCreatePermissionFail = '[BE][Subscription] has subscription create permission fail',

	InitSubscriptionConfig = '[Subscription] Init subscription config',
	RefreshSubscriptionDatablockList = '[Subscription] Refresh subscription datablock list',
	FetchSubscriptionDataBlocksSuccess = '[BE][Subscription] Fetch subscription datablock list success',
	FetchSubscriptionDataBlocksFail = '[BE][Subscription] Fetch subscription datablock list fail',
	GetDatablockColumns = '[Subscription] Get datablock columns',
	FetchDatablockColumnsSuccess = '[BE][Subscription] Fetch datablock columns success',
	FetchDatablockColumnsFail = '[BE][Subscription] Fetch datablock columns fail',
	GetSourceColumns = '[Subscription] Get source datablock columns list',
	RefreshSourceColumns = '[Subscription] Refresh source datablock columns list',
	FetchSourceColumnsSuccess = '[BE][Subscription] Fetch source datablock columns list success',
	FetchSourceColumnsFail = '[BE][Subscription] Fetch source datablock columns list fail',
	GetAvailableProjects = '[Subscription] Get available project list',
	RefreshAvailableProjects = '[Subscription] Refresh available project list',
	FetchAvailableProjectsSuccess = '[BE][Subscription] Fetch available project list success',
	FetchAvailableProjectsFail = '[BE][Subscription] Fetch available project list fail',
	OpenCreateNewSubscriptionModal = '[Subscription] Open create new subscription modal',
	OpenCreateNewSubscriptionModalFromDatablock = '[Subscription] Open create new subscription modal from datablock',
	CloseNewSubscriptionModal = '{Subscription] Close new subscription modal',
	SaveNewSubscription = '[Subscription] Save new Subscription',
	SaveNewSubscriptionSuccess = '[BE][Subscription] Save new subscription success',
	SaveNewSubscriptionFail = '[BE][Subscription] Save new subscription fail',

	NavigateToSubscriptionList = '[Subscription] Navigate to subscription list',
	NavigateToSubscriptionDetails = '[Subscription] Navigate to subscription details',
	NavigateToSubscriptionCreate = '[Subscription] Navigate to subscription create',
	NavigateToSubscriptionCreateFromDatablock = '[Subscription] Navigate to subscription create from datablock',
	NavigateToSubscriptionSimulation = '[Subscription] Navigate to subscription simulator',
	NavigateToSubscriptionSimulationWithSelectedProfile = '[Subscription] Navigate to subscription simulator with selected profile',
	NavigateToSubscriptionSimulationWithSelectedColumnModel = '[Subscription] Navigate to subscription simulator with selected column model',

	SelectSubscription = '[Subscription] Select subscription view',
	InitSubscriptionView = '[Subscription] Init subscription view',
	RefreshSubscriptionView = '[Subscription] Refresh subscription view',
	FetchSubscriptionSuccess = '[BE][Subscription] Fetch subscription success',
	FetchSubscriptionFail = '[BE][Subscription] Fetch subscription fail',
	OpenDeleteConfirmPopupOnView = '[Subscription] Open delete confirm popup on view',
	UpdateSubscriptionColumns = '[Subscription] Update subscription columns',
	UpdateSubscriptionColumnsSuccess = '[BE][Subscription] Update subscription columns success',
	UpdateSubscriptionColumnsFail = '[BE][Subscription] Update subscription columns fail',
	OpenUpdateInternalMetadataPopup = '[Subscription] Open update subscription internal metadata popup',
	UpdateInternalMetadata = '[Subscription] Update subscription internal metadata',
	UpdateInternalMetadataSuccess = '[BE][Subscription] Update subscription internal metadata success',
	UpdateInternalMetadataFail = '[BE][Subscription] Update subscription internal metadata fail',
	OpenUpdatePublicMetadataPopup = '[Subscription] Open update subscription public metadata popup',
	UpdatePublicMetadata = '[Subscription] Update subscription public metadata',
	UpdatePublicMetadataSuccess = '[BE][Subscription] Update subscription public metadata success',
	UpdatePublicMetadataFail = '[BE][Subscription] Update subscription public metadata fail',
	RefreshSubscriptionColumns = '[Subscription] Refresh subscription column list',
	FetchSubscriptionColumnsSuccess = '[BE][Subscription] Fetch subscription column list success',
	FetchSubscriptionColumnsFail = '[BE][Subscription] Fetch subscription column list fail',
	OpenInitializeConfirmPopup = '[Subscription] Open initialize confirm popup',
	UpdateSubscriptionActionInProgress = '[WS][Subscription] Update subscription action in progress',
	CreateNewProfile = '[Subscription] Create new profile',
	CreateNewProfileSuccess = '[BE][Subscription] Create new profile success',
	CreateNewProfileFail = '[BE][Subscription] Create new profile fail',
	RefreshSubscriptionProfiles = '[Subscription] Refresh subscription profile list',
	RefreshSubscriptionProfilesWithModels = '[Subscription] Refresh subscription profile list, column model list, filter model list',
	FetchSubscriptionProfilesSuccess = '[BE][Subscription] Fetch subscription profile list success',
	FetchSubscriptionProfilesFail = '[BE][Subscription] Fetch subscription profile list fail',
	ReloadProfilesAfterAction = '[Subscription] Reload profiles after action',
	ReloadProfilesAfterActionSuccess = '[BE][Subscription] Reload profiles after action success',
	OpenDeleteProfileConfirmPopup = '[Subscription] Open delete profile confirm popup',
	CreateNewColumnModel = '[Subscription] Create new column model',
	CreateNewColumnModelSuccess = '[BE][Subscription] Create new column model success',
	CreateNewColumnModelFail = '[BE][Subscription] Create new column model fail',
	RefreshSubscriptionColumnModels = '[Subscription] Refresh subscription column model list',
	FetchSubscriptionColumnModelsSuccess = '[BE][Subscription] Fetch subscription column model list success',
	FetchSubscriptionColumnModelsFail = '[BE][Subscription] Fetch subscription column model list fail',
	OpenDeleteColumnModelConfirmPopup = '[Subscription] Open delete column model confirm popup',
	CreateNewFilterModel = '[Subscription] Create new filter model',
	CreateNewFilterModelSuccess = '[BE][Subscription] Create new filter model success',
	CreateNewFilterModelFail = '[BE][Subscription] Create new filter model fail',
	RefreshSubscriptionFilterModels = '[Subscription] Refresh subscription filter model list',
	FetchSubscriptionFilterModelsSuccess = '[BE][Subscription] Fetch subscription filter model list success',
	FetchSubscriptionFilterModelsFail = '[BE][Subscription] Fetch subscription filter model list fail',
	OpenDeleteFilterModelConfirmPopup = '[Subscription] Open delete filter model confirm popup',
	GetPublicationHistories = '[Subscription] Get publication histories',
	RefreshPublicationHistories = '[Subscription] Refresh publication histories',
	FetchPublicationHistoriesSuccess = '[BE][Subscription] Fetch publication histories success',
	FetchPublicationHistoriesFail = '[BE][Subscription] Fetch publication histories fail',
	RefreshConsumedProjects = '[Subscription] Refresh consumed projects',
	FetchConsumedProjectsSuccess = '[BE][Subscription] Fetch consumed projects success',
	FetchConsumedProjectsFail = '[BE][Subscription] Fetch consumed projects fail',
	DeleteConsumedProject = '[Subscription] Delete consumed project',
	DeleteBulkConsumedProject = '[Subscription] Delete bulk consumed project',
	DeleteConsumedProjectSuccess = '[BE][Subscription] Delete consumed project success',
	DeleteConsumedProjectFail = '[BE][Subscription] Delete consumed project fail',
	GetProjects = '[Subscription] Get projects',
	RefreshProjects = '[Subscription] Refresh projects',
	FetchProjectsSuccess = '[BE][Subscription] Fetch projects success',
	FetchProjectsFail = '[BE][Subscription] Fetch projects fail',
	SaveNewConsumers = '[Subscription] Save new consumers',
	SaveNewConsumersSuccess = '[BE][Subscription] Save new consumers success',
	SaveNewConsumersFail = '[BE][Subscription] Save new consumers fail',
	UpdateConsumer = '[Subscription] Update consumer',
	UpdateConsumerSuccess = '[BE][Subscription] Update consumer success',
	UpdateConsumerFail = '[BE][Subscription] Update consumer fail',
	ToggleProjectConsume = '[Subscription] Toggle project consume',
	ToggleBulkProjectConsume = '[Subscription] Toggle bulk project consume',
	ToggleProjectConsumeSuccess = '[BE][Subscription] Toggle project consume success',
	ToggleProjectConsumeFail = '[BE][Subscription] Toggle project consume fail',
	ResetSubscriptionView = '[Subscription] Reset subscription view',
	RefreshPublishedSubscription = '[Subscription] Refresh published subscription',
	RefreshPublishedSubscriptionSuccess = '[BE][Subscription] Refresh published subscription success',
	RefreshPublishedSubscriptionFail = '[BE][Subscription] Refresh published subscription fail',
	GetConsumedProfileColumns = '[Subscription] Get consumed profile columns',
	FetchConsumedProfileColumnsSuccess = '[BE][Subscription] Fetch consumed profile columns success',
	FetchConsumedProfileColumnsFail = '[BE][Subscription] Fetch consumed profile columns fail',

	GetCurrentSubscriptionSimulationConfig = '[Subscription][Simulation] Get config for current subscription',
	FetchCurrentSubscriptionSimulationConfigSuccess = '[BE][Subscription][Simulation] Fetch current subscription simulation config success',
	FetchCurrentSubscriptionSimulationConfigFail = '[BE][Subscription][Simulation] Fetch current subscription simulation config fail',
	GetProfilesModelsForSimulation = '[Subscription][Simulation] Get profiles & models for simulation',
	FetchProfilesModelsForSimulationSuccess = '[BE][Subscription][Simulation] Fetch profiles & models for simulation success',
	FetchProfilesModelsForSimulationFail = '[BE][Subscription][Simulation] Fetch profiles & models for simulation fail',
	RefreshProfilesForSimulation = '[Subscription][Simulation] Refresh profiles for simulation',
	FetchProfilesForSimulationSuccess = '[BE][Subscription][Simulation] Fetch profiles for simulation success',
	FetchProfilesForSimulationFail = '[BE][Subscription][Simulation] Fetch profiles for simulation fail',
	RefreshColumnModelsForSimulation = '[Subscription][Simulation] Refresh column models for simulation',
	FetchColumnModelsForSimulationSuccess = '[BE][Subscription][Simulation] Fetch column models for simulation success',
	FetchColumnModelsForSimulationFail = '[BE][Subscription][Simulation] Fetch column models for simulation fail',
	RefreshFilterModelsForSimulation = '[Subscription][Simulation] Refresh filter models for simulation',
	FetchFilterModelsForSimulationSuccess = '[BE][Subscription][Simulation] Fetch filter models for simulation success',
	FetchFilterModelsForSimulationFail = '[BE][Subscription][Simulation] Fetch filter models for simulation fail',
	GetSubscriptionSimulationData = '[Subscription] Get subscription simulation data',
	FetchSubscriptionSimulationDataSuccess = '[BE][Subscription] Fetch subscription simulation data success',
	FetchSubscriptionSimulationDataFail = '[BE][Subscription] Fetch subscription simulation data fail',
	ResetSimulationData = '[Subscription] Reset simulation data',
	ResetSubscriptionSimulation = '[Subscription] Reset subscription simulation',

	InitializeSubscription = '[Subscription] Initialize subscription',
	InitializeSubscriptionOnList = '[Subscription] Initialize subscription on list',
	InitializeSubscriptionSuccess = '[BE][Subscription] Initialize subscription success',
	InitializeSubscriptionFail = '[BE][Subscription] Initialize subscription fail',

	ActivateSubscription = '[Subscription] Activate subscription',
	ActivateBulkSubscription = '[Subscription] Activate bulk subscription',
	ActivateSubscriptionSuccess = '[BE][Subscription] Activate subscription success',
	ActivateBulkSubscriptionSuccess = '[BE][Subscription] Activate bulk subscription success',
	ActivateSubscriptionFail = '[BE][Subscription] Activate subscription fail',

	DeactivateSubscription = '[Subscription] Deactivate subscription',
	DeactivateBulkSubscription = '[Subscription] Deactivate bulk subscription',
	DeactivateSubscriptionSuccess = '[BE][Subscription] Deactivate subscription success',
	DeactivateBulkSubscriptionSuccess = '[BE][Subscription] Deactivate bulk subscription success',
	DeactivateSubscriptionFail = '[BE][Subscription] Deactivate subscription fail',

	UpdateSubscriptionData = '[Subscription] Update subscription data',
	UpdateSubscriptionDataOnList = '[Subscription] Update subscription data on list',
	UpdateSubscriptionDataSuccess = '[BE][Subscription] Update subscription data success',
	UpdateSubscriptionDataFail = '[BE][Subscription] Update subscription data fail',

	ClearSubscriptionData = '[Subscription] Clear subscription data',
	ClearBulkSubscriptionData = '[Subscription] Clear bulk subscription data',
	ClearSubscriptionDataSuccess = '[BE][Subscription] Clear subscription data success',
	ClearBulkSubscriptionDataSuccess = '[BE][Subscription] Clear bulk subscription data success',
	ClearSubscriptionDataFail = '[BE][Subscription] Clear subscription data fail',

	DeleteSubscription = '[Subscription] Delete subscription',
	DeleteBulkSubscription = '[Subscription] Delete bulk subscription',
	DeleteSubscriptionSuccess = '[BE][Subscription] Delete subscription success',
	DeleteSubscriptionFail = '[BE][Subscription] Delete subscription fail',

	UpdateProfile = '[Subscription] Update profile',
	UpdateProfileSuccess = '[BE][Subscription] Update profile success',
	UpdateProfileFail = '[BE][Subscription] Update profile fail',
	DeleteProfile = '[Subscription] Delete profile',
	DeleteBulkProfile = '[Subscription] Delete bulk profile',
	DeleteProfileSuccess = '[BE][Subscription] Delete profile success',
	DeleteProfileFail = '[BE][Subscription] Delete profile fail',

	UpdateColumnModel = '[Subscription] Update column Model',
	UpdateColumnModelSuccess = '[BE][Subscription] Update column Model success',
	UpdateColumnModelFail = '[BE][Subscription] Update column model fail',
	DeleteColumnModel = '[Subscription] Delete column model',
	DeleteBulkColumnModel = '[Subscription] Delete bulk column model',
	DeleteColumnModelSuccess = '[BE][Subscription] Delete column model success',
	DeleteColumnModelFail = '[BE][Subscription] Delete column model fail',

	UpdateFilterModel = '[Subscription] Update filter Model',
	UpdateFilterModelSuccess = '[BE][Subscription] Update filter Model success',
	UpdateFilterModelFail = '[BE][Subscription] Update filter model fail',
	DeleteFilterModel = '[Subscription] Delete filter model',
	DeleteBulkFilterModel = '[Subscription] Delete bulk filter model',
	DeleteFilterModelSuccess = '[BE][Subscription] Delete filter model success',
	DeleteFilterModelFail = '[BE][Subscription] Delete filter model fail',

	InitPermissionsConfig = '[Subscription] Init permissions config',
	RefreshMembersAccess = '[Subscription] Refresh members access',
	FetchMembersAccessSuccess = '[BE][Subscription] Fetch member access success',
	FetchMembersAccessFail = '[BE][Subscription] Fetch member access fail',
	UpdateMembersAccess = '[Subscription] Update members access',
	UpdateMemberAccessSuccess = '[BE][Subscription] Update members access success',
	UpdateMemberAccessFail = '[BE][Subscription] Update members access fail',

	GetSwitchableConsumedProjects = '[Subscription] Get switchable consumed projects',
	RefreshSwitchableConsumedProjects = '[Subscription] Refresh switchable consumed projects',
	GetSwitchableConsumedProjectsForSubscription = '[Subscription] Get switchable consumed projects for subscription',
	RefreshSwitchableConsumedProjectsForSubscription = '[Subscription] Refresh switchable consumed projects for subscription',
	FetchSwitchableConsumedProjectsSuccess = '[BE][Subscription] Fetch switchable consumed projects success',
	FetchSwitchableConsumedProjectsFail = '[BE][Subscription] Fetch switchable consumed projects fail',
	SwitchVersionForConsumedProjectsOnView = '[Subscription] Switch version for consumed projects on view',
	SwitchVersionForConsumedProjectsOnViewSuccess = '[BE][Subscription] Switch version for consumed projects success on view',
	SwitchVersionForConsumedProjectsOnViewFail = '[BE][Subscription] Switch version for consumed projects fail on view',
	SwitchVersionForConsumedProjects = '[Subscription] Switch version for consumed projects',
	SwitchVersionForConsumedProjectsSuccess = '[BE][Subscription] Switch version for consumed projects success',
	SwitchVersionForConsumedProjectsFail = '[BE][Subscription] Switch version for consumed projects fail',
}

export const initSubscriptions = createAction(
	SubscriptionActions.InitSubscriptions
);

export const getSubscriptions = createAction(
	SubscriptionActions.GetSubscriptions,
	props<{
		size: number;
		page: number;
		search: string;
		filteredFields: Array<DxFilterField>;
		sortedFields: Array<DxSortField>;
	}>()
);

export const refreshSubscriptions = createAction(
	SubscriptionActions.RefreshSubscriptions,
	props<{ subscriptionId: number }>()
);

export const refreshSubscriptionsSuccess = createAction(
	SubscriptionActions.RefreshSubscriptionsSuccess,
	props<{ subscriptions: Map<string, SubscriptionItemEntity> }>()
);

export const fetchSubscriptionsSuccess = createAction(
	SubscriptionActions.FetchSubscriptionsSuccess,
	props<{ subscriptions: Map<string, SubscriptionItemEntity>; total: number }>()
);

export const fetchSubscriptionsFail = createAction(
	SubscriptionActions.FetchSubscriptionsFail,
	props<{ error: unknown }>()
);

export const hasSubscriptionCreatePermissionSuccess = createAction(
	SubscriptionActions.HasSubscriptionCreatePermissionSuccess,
	props<{ canCreate: boolean }>()
);

export const hasSubscriptionCreatePermissionFail = createAction(
	SubscriptionActions.HasSubscriptionCreatePermissionFail,
	props<{ error: unknown }>()
);

export const initSubscriptionStepper = createAction(
	SubscriptionActions.InitSubscriptionConfig
);

export const refreshSubscriptionDatablockList = createAction(
	SubscriptionActions.RefreshSubscriptionDatablockList
);

export const fetchSubscriptionDataBlocksSuccess = createAction(
	SubscriptionActions.FetchSubscriptionDataBlocksSuccess,
	props<{ datablocks: Map<string, SubscriptionDatablockItemEntity> }>()
);

export const fetchSubscriptionDataBlocksFail = createAction(
	SubscriptionActions.FetchSubscriptionDataBlocksFail,
	props<{ error: unknown }>()
);

export const getDatablockColumns = createAction(
	SubscriptionActions.GetDatablockColumns,
	props<{ datablockId: number }>()
);

export const fetchDatablockColumnsSuccess = createAction(
	SubscriptionActions.FetchDatablockColumnsSuccess,
	props<{ datablockId: number; datablockColumns: Array<DatablockColumn> }>()
);

export const fetchDatablockColumnsFail = createAction(
	SubscriptionActions.FetchDatablockColumnsFail,
	props<{ error: unknown }>()
);

export const getSourceColumns = createAction(
	SubscriptionActions.GetSourceColumns,
	props<{ datablockId: number }>()
);

export const refreshSourceColumns = createAction(
	SubscriptionActions.RefreshSourceColumns,
	props<{ datablockId: number }>()
);

export const fetchSourceColumnsSuccess = createAction(
	SubscriptionActions.FetchSourceColumnsSuccess,
	props<{ columns: Map<string, ColumnConfigItemEntity> }>()
);

export const fetchSourceColumnsFail = createAction(
	SubscriptionActions.FetchSourceColumnsFail,
	props<{ error: unknown }>()
);

export const getAvailableProjects = createAction(
	SubscriptionActions.GetAvailableProjects
);

export const refreshAvailableProjects = createAction(
	SubscriptionActions.RefreshAvailableProjects
);

export const fetchAvailableProjectsSuccess = createAction(
	SubscriptionActions.FetchAvailableProjectsSuccess,
	props<{ projects: Map<string, SubscriptionProjectItemEntity> }>()
);

export const fetchAvailableProjectsFail = createAction(
	SubscriptionActions.FetchAvailableProjectsFail,
	props<{ error: unknown }>()
);

export const openCreateNewSubscriptionModal = createAction(
	SubscriptionActions.OpenCreateNewSubscriptionModal
);

export const openCreateNewSubscriptionModalFromDatablock = createAction(
	SubscriptionActions.OpenCreateNewSubscriptionModalFromDatablock,
	props<{ datablockId: number; datablockLabel: string }>()
);

export const closeNewSubscriptionModal = createAction(
	SubscriptionActions.CloseNewSubscriptionModal,
	props<{ datablockId: number }>()
);

export const navigateToSubscriptionList = createAction(
	SubscriptionActions.NavigateToSubscriptionList
);

export const navigateToSubscriptionDetails = createAction(
	SubscriptionActions.NavigateToSubscriptionDetails,
	props<{ subscriptionId: number }>()
);

export const navigateToSubscriptionCreate = createAction(
	SubscriptionActions.NavigateToSubscriptionCreate
);

export const navigateToSubscriptionCreateFromDatablock = createAction(
	SubscriptionActions.NavigateToSubscriptionCreateFromDatablock,
	props<{ datablockId: number; datablockLabel: string }>()
);

export const navigateToSubscriptionSimulation = createAction(
	SubscriptionActions.NavigateToSubscriptionSimulation,
	props<{ subscriptionId: number }>()
);

export const navigateToSubscriptionSimulationWithSelectedProfile = createAction(
	SubscriptionActions.NavigateToSubscriptionSimulationWithSelectedProfile,
	props<{ subscriptionId: number; profile: string }>()
);

export const navigateToSubscriptionSimulationWithSelectedColumnModel =
	createAction(
		SubscriptionActions.NavigateToSubscriptionSimulationWithSelectedColumnModel,
		props<{ subscriptionId: number; columnModel: string }>()
	);

export const selectSubscription = createAction(
	SubscriptionActions.SelectSubscription,
	props<{ subscriptionId: number }>()
);

export const initSubscriptionView = createAction(
	SubscriptionActions.InitSubscriptionView,
	props<{ subscriptionId: number }>()
);

export const refreshSubscriptionView = createAction(
	SubscriptionActions.RefreshSubscriptionView,
	props<{ subscriptionId: number }>()
);

export const fetchSubscriptionSuccess = createAction(
	SubscriptionActions.FetchSubscriptionSuccess,
	props<{ subscription: ViewDetailsEntity }>()
);

export const fetchSubscriptionFail = createAction(
	SubscriptionActions.FetchSubscriptionFail,
	props<{ error: unknown }>()
);

export const openDeleteConfirmPopupOnView = createAction(
	SubscriptionActions.OpenDeleteConfirmPopupOnView
);

export const updateSubscriptionColumns = createAction(
	SubscriptionActions.UpdateSubscriptionColumns,
	props<{
		columns: Map<string, ColumnConfigItemEntity>;
	}>()
);

export const updateSubscriptionColumnsSuccess = createAction(
	SubscriptionActions.UpdateSubscriptionColumnsSuccess,
	props<{
		subscriptionId: number;
	}>()
);

export const updateSubscriptionColumnsFail = createAction(
	SubscriptionActions.UpdateSubscriptionColumnsFail,
	props<{ error: unknown }>()
);

export const saveNewSubscription = createAction(
	SubscriptionActions.SaveNewSubscription,
	props<{
		datablockId: number;
		internalMetadata: InternalMetadataEntity;
		publicMetadata: PublicMetadataEntity;
		columns: Map<string, ColumnConfigItemEntity>;
		profile: ProfileItemEntity;
		columnModel: ColumnModelItemEntity;
		filterModels: Array<FilterModelItemEntity>;
	}>()
);

export const saveNewSubscriptionSuccess = createAction(
	SubscriptionActions.SaveNewSubscriptionSuccess,
	props<{ subscriptionId: number }>()
);

export const saveNewSubscriptionFail = createAction(
	SubscriptionActions.SaveNewSubscriptionFail,
	props<{ error: unknown }>()
);

export const openUpdateInternalMetadataPopup = createAction(
	SubscriptionActions.OpenUpdateInternalMetadataPopup
);

export const updateInternalMetadata = createAction(
	SubscriptionActions.UpdateInternalMetadata,
	props<{ internalMetadata: InternalMetadataEntity }>()
);

export const updateInternalMetadataSuccess = createAction(
	SubscriptionActions.UpdateInternalMetadataSuccess,
	props<{ internalMetadata: InternalMetadataEntity }>()
);

export const updateInternalMetadataFail = createAction(
	SubscriptionActions.UpdateInternalMetadataFail,
	props<{ error: unknown }>()
);

export const openUpdatePublicMetadataPopup = createAction(
	SubscriptionActions.OpenUpdatePublicMetadataPopup
);

export const updatePublicMetadata = createAction(
	SubscriptionActions.UpdatePublicMetadata,
	props<{ publicMetadata: PublicMetadataEntity }>()
);

export const updatePublicMetadataSuccess = createAction(
	SubscriptionActions.UpdatePublicMetadataSuccess,
	props<{ publicMetadata: PublicMetadataEntity }>()
);

export const updatePublicMetadataFail = createAction(
	SubscriptionActions.UpdatePublicMetadataFail,
	props<{ error: unknown }>()
);

export const refreshSubscriptionColumns = createAction(
	SubscriptionActions.RefreshSubscriptionColumns
);

export const fetchSubscriptionColumnsSuccess = createAction(
	SubscriptionActions.FetchSubscriptionColumnsSuccess,
	props<{ columns: Map<string, ColumnConfigItemEntity> }>()
);

export const fetchSubscriptionColumnsFail = createAction(
	SubscriptionActions.FetchSubscriptionColumnsFail,
	props<{ error: unknown }>()
);

export const openInitializeConfirmPopup = createAction(
	SubscriptionActions.OpenInitializeConfirmPopup
);

export const updateSubscriptionActionInProgress = createAction(
	SubscriptionActions.UpdateSubscriptionActionInProgress,
	props<{
		subscriptionId: number;
		actionInProgress: SubscriptionAction;
	}>()
);

export const createNewProfile = createAction(
	SubscriptionActions.CreateNewProfile,
	props<{
		profile: ProfileItemEntity;
		columnModel: ColumnModelItemEntity | string;
		filterModels: Array<FilterModelItemEntity | string>;
	}>()
);

export const createNewProfileSuccess = createAction(
	SubscriptionActions.CreateNewProfileSuccess
);

export const createNewProfileFail = createAction(
	SubscriptionActions.CreateNewProfileFail,
	props<{ error: unknown }>()
);

export const refreshSubscriptionProfiles = createAction(
	SubscriptionActions.RefreshSubscriptionProfiles
);

export const refreshSubscriptionProfilesWithModels = createAction(
	SubscriptionActions.RefreshSubscriptionProfilesWithModels
);

export const fetchSubscriptionProfilesSuccess = createAction(
	SubscriptionActions.FetchSubscriptionProfilesSuccess,
	props<{ profiles: Map<string, ProfileItemEntity> }>()
);

export const reloadProfilesAfterAction = createAction(
	SubscriptionActions.ReloadProfilesAfterAction
);

export const reloadProfilesAfterActionSuccess = createAction(
	SubscriptionActions.ReloadProfilesAfterActionSuccess,
	props<{
		profiles: Map<string, ProfileItemEntity>;
	}>()
);

export const fetchSubscriptionProfilesFail = createAction(
	SubscriptionActions.FetchSubscriptionProfilesFail,
	props<{ error: unknown }>()
);

export const openDeleteProfileConfirmPopup = createAction(
	SubscriptionActions.OpenDeleteProfileConfirmPopup,
	props<{
		profileId: string | Array<string>;
		profileLabel: string;
	}>()
);

export const createNewColumnModel = createAction(
	SubscriptionActions.CreateNewColumnModel,
	props<{
		columnModel: ColumnModelItemEntity;
	}>()
);

export const createNewColumnModelSuccess = createAction(
	SubscriptionActions.CreateNewColumnModelSuccess
);

export const createNewColumnModelFail = createAction(
	SubscriptionActions.CreateNewColumnModelFail,
	props<{ error: unknown }>()
);

export const refreshSubscriptionColumnModels = createAction(
	SubscriptionActions.RefreshSubscriptionColumnModels
);

export const fetchSubscriptionColumnModelsSuccess = createAction(
	SubscriptionActions.FetchSubscriptionColumnModelsSuccess,
	props<{ columnModels: Map<string, ColumnModelItemEntity> }>()
);

export const fetchSubscriptionColumnModelsFail = createAction(
	SubscriptionActions.FetchSubscriptionColumnModelsFail,
	props<{ error: unknown }>()
);

export const openDeleteColumnModelConfirmPopup = createAction(
	SubscriptionActions.OpenDeleteColumnModelConfirmPopup,
	props<{
		columnModelId: string | Array<string>;
		columnModelLabel: string;
	}>()
);

export const createNewFilterModel = createAction(
	SubscriptionActions.CreateNewFilterModel,
	props<{ model: FilterModelItemEntity }>()
);

export const createNewFilterModelSuccess = createAction(
	SubscriptionActions.CreateNewFilterModelSuccess
);

export const createNewFilterModelFail = createAction(
	SubscriptionActions.CreateNewFilterModelFail,
	props<{ error: unknown }>()
);

export const refreshSubscriptionFilterModels = createAction(
	SubscriptionActions.RefreshSubscriptionFilterModels
);

export const fetchSubscriptionFilterModelsSuccess = createAction(
	SubscriptionActions.FetchSubscriptionFilterModelsSuccess,
	props<{ filterModels: Map<string, FilterModelItemEntity> }>()
);

export const fetchSubscriptionFilterModelsFail = createAction(
	SubscriptionActions.FetchSubscriptionFilterModelsFail,
	props<{ error: unknown }>()
);

export const openDeleteFilterModelConfirmPopup = createAction(
	SubscriptionActions.OpenDeleteFilterModelConfirmPopup,
	props<{
		filterModelId: string | Array<string>;
		filterModelLabel: string;
	}>()
);

export const getPublicationHistories = createAction(
	SubscriptionActions.GetPublicationHistories
);

export const refreshPublicationHistories = createAction(
	SubscriptionActions.RefreshPublicationHistories
);

export const fetchPublicationHistoriesSuccess = createAction(
	SubscriptionActions.FetchPublicationHistoriesSuccess,
	props<{ histories: Map<string, HistoryItemEntity> }>()
);

export const fetchPublicationHistoriesFail = createAction(
	SubscriptionActions.FetchPublicationHistoriesFail,
	props<{ error: unknown }>()
);

export const refreshConsumedProjects = createAction(
	SubscriptionActions.RefreshConsumedProjects,
	props<{ filterVersion: FilterVersion }>()
);

export const fetchConsumedProjectsSuccess = createAction(
	SubscriptionActions.FetchConsumedProjectsSuccess,
	props<{ consumedProjects: Map<string, ConsumedProjectItemEntity> }>()
);

export const fetchConsumedProjectsFail = createAction(
	SubscriptionActions.FetchConsumedProjectsFail,
	props<{ error: unknown }>()
);

export const deleteConsumedProject = createAction(
	SubscriptionActions.DeleteConsumedProject,
	props<{ consumedProjectId: number }>()
);

export const deleteBulkConsumedProject = createAction(
	SubscriptionActions.DeleteBulkConsumedProject,
	props<{ consumedProjectIds: Array<number> }>()
);

export const deleteConsumedProjectSuccess = createAction(
	SubscriptionActions.DeleteConsumedProjectSuccess
);

export const deleteConsumedProjectFail = createAction(
	SubscriptionActions.DeleteConsumedProjectFail,
	props<{ error: unknown }>()
);

export const getProjects = createAction(SubscriptionActions.GetProjects);

export const refreshProjects = createAction(
	SubscriptionActions.RefreshProjects
);

export const fetchProjectsSuccess = createAction(
	SubscriptionActions.FetchProjectsSuccess,
	props<{ projects: Map<string, ProjectItemEntity> }>()
);

export const fetchProjectsFail = createAction(
	SubscriptionActions.FetchProjectsFail,
	props<{ error: unknown }>()
);

export const saveNewConsumers = createAction(
	SubscriptionActions.SaveNewConsumers,
	props<{ profileUuid: string; consumerIds: Array<string> }>()
);

export const saveNewConsumersSuccess = createAction(
	SubscriptionActions.SaveNewConsumersSuccess
);

export const saveNewConsumersFail = createAction(
	SubscriptionActions.SaveNewConsumersFail,
	props<{ error: unknown }>()
);

export const updateConsumer = createAction(
	SubscriptionActions.UpdateConsumer,
	props<{ profileUuid: string; projectUuid: string }>()
);

export const updateConsumerSuccess = createAction(
	SubscriptionActions.UpdateConsumerSuccess
);

export const updateConsumerFail = createAction(
	SubscriptionActions.UpdateConsumerFail,
	props<{ error: unknown }>()
);

export const toggleProjectConsume = createAction(
	SubscriptionActions.ToggleProjectConsume,
	props<{ id: number; isActive: boolean }>()
);

export const toggleBulkProjectConsume = createAction(
	SubscriptionActions.ToggleBulkProjectConsume,
	props<{ ids: Array<number>; isActive: boolean }>()
);

export const toggleProjectConsumeSuccess = createAction(
	SubscriptionActions.ToggleProjectConsumeSuccess
);

export const toggleProjectConsumeFail = createAction(
	SubscriptionActions.ToggleProjectConsumeFail,
	props<{ error: unknown }>()
);

export const resetSubscriptionView = createAction(
	SubscriptionActions.ResetSubscriptionView
);

export const refreshPublishedSubscription = createAction(
	SubscriptionActions.RefreshPublishedSubscription
);

export const refreshPublishedSubscriptionSuccess = createAction(
	SubscriptionActions.RefreshPublishedSubscriptionSuccess,
	props<{ publishedSubscription: PublishedSubscriptionEntity }>()
);

export const refreshPublishedSubscriptionFail = createAction(
	SubscriptionActions.RefreshPublishedSubscriptionFail,
	props<{ error: unknown }>()
);

export const getConsumedProfileColumns = createAction(
	SubscriptionActions.GetConsumedProfileColumns,
	props<{ consumerId: number }>()
);

export const fetchConsumedProfileColumnsSuccess = createAction(
	SubscriptionActions.FetchConsumedProfileColumnsSuccess,
	props<{ consumerId: number; profileColumns: Array<DatablockColumn> }>()
);

export const fetchConsumedProfileColumnsFail = createAction(
	SubscriptionActions.FetchConsumedProfileColumnsFail,
	props<{ error: unknown }>()
);

export const getCurrentSubscriptionSimulationConfig = createAction(
	SubscriptionActions.GetCurrentSubscriptionSimulationConfig,
	props<{ subscriptionId: number }>()
);

export const fetchCurrentSubscriptionSimulationConfigSuccess = createAction(
	SubscriptionActions.FetchCurrentSubscriptionSimulationConfigSuccess,
	props<{
		subscriptionId: number;
		subscriptionStatus: SubscriptionStatus;
		metadata: PublicMetadataEntity;
	}>()
);

export const fetchCurrentSubscriptionSimulationConfigFail = createAction(
	SubscriptionActions.FetchCurrentSubscriptionSimulationConfigFail,
	props<{ error: unknown }>()
);

export const getProfilesModelsForSimulation = createAction(
	SubscriptionActions.GetProfilesModelsForSimulation,
	props<{ subscriptionId: number }>()
);

export const fetchProfilesModelsForSimulationSuccess = createAction(
	SubscriptionActions.FetchProfilesModelsForSimulationSuccess,
	props<{
		profiles: Map<string, ProfileItemEntity>;
		columnModels: Map<string, ColumnModelItemEntity>;
		filterModels: Map<string, FilterModelItemEntity>;
		subscriptionColumns: Map<string, ColumnConfigItemEntity>;
	}>()
);

export const fetchProfilesModelsForSimulationFail = createAction(
	SubscriptionActions.FetchProfilesModelsForSimulationFail,
	props<{ error: unknown }>()
);

export const refreshProfilesForSimulation = createAction(
	SubscriptionActions.RefreshProfilesForSimulation
);

export const fetchProfilesForSimulationSuccess = createAction(
	SubscriptionActions.FetchProfilesForSimulationSuccess,
	props<{
		profiles: Map<string, ProfileItemEntity>;
	}>()
);

export const fetchProfilesForSimulationFail = createAction(
	SubscriptionActions.FetchProfilesForSimulationFail,
	props<{ error: unknown }>()
);

export const refreshColumnModelsForSimulation = createAction(
	SubscriptionActions.RefreshColumnModelsForSimulation
);

export const fetchColumnModelsForSimulationSuccess = createAction(
	SubscriptionActions.FetchColumnModelsForSimulationSuccess,
	props<{
		columnModels: Map<string, ColumnModelItemEntity>;
	}>()
);

export const fetchColumnModelsForSimulationFail = createAction(
	SubscriptionActions.FetchColumnModelsForSimulationFail,
	props<{ error: unknown }>()
);

export const refreshFilterModelsForSimulation = createAction(
	SubscriptionActions.RefreshFilterModelsForSimulation
);

export const fetchFilterModelsForSimulationSuccess = createAction(
	SubscriptionActions.FetchFilterModelsForSimulationSuccess,
	props<{
		filterModels: Map<string, FilterModelItemEntity>;
	}>()
);

export const fetchFilterModelsForSimulationFail = createAction(
	SubscriptionActions.FetchFilterModelsForSimulationFail,
	props<{ error: unknown }>()
);

export const getSubscriptionSimulationData = createAction(
	SubscriptionActions.GetSubscriptionSimulationData,
	props<{
		profile: ProfileItemEntity | string;
		dataSource: SimulationDataSource;
		displayColumns: Array<string>;
		sortColumns: Array<SimulationSortColumnType>;
	}>()
);

export const fetchSubscriptionSimulationDataSuccess = createAction(
	SubscriptionActions.FetchSubscriptionSimulationDataSuccess,
	props<{ simulationData: SimulationResultEntity }>()
);

export const fetchSubscriptionSimulationDataFail = createAction(
	SubscriptionActions.FetchSubscriptionSimulationDataFail,
	props<{ error: unknown }>()
);

export const resetSimulationData = createAction(
	SubscriptionActions.ResetSimulationData
);

export const resetSubscriptionSimulation = createAction(
	SubscriptionActions.ResetSubscriptionSimulation
);

export const initializeSubscription = createAction(
	SubscriptionActions.InitializeSubscription,
	props<{ subscriptionId: number; operationType: OperationType }>()
);

export const initializeSubscriptionOnList = createAction(
	SubscriptionActions.InitializeSubscriptionOnList,
	props<{ subscriptionId: number; operationType: OperationType }>()
);

export const initializeSubscriptionSuccess = createAction(
	SubscriptionActions.InitializeSubscriptionSuccess
);

export const initializeSubscriptionFail = createAction(
	SubscriptionActions.InitializeSubscriptionFail,
	props<{ error: unknown }>()
);

export const activateSubscription = createAction(
	SubscriptionActions.ActivateSubscription,
	props<{
		subscriptionId: number;
		inViewMode: boolean;
	}>()
);

export const activateBulkSubscription = createAction(
	SubscriptionActions.ActivateBulkSubscription,
	props<{ subscriptionIds: Array<number> }>()
);

export const activateSubscriptionSuccess = createAction(
	SubscriptionActions.ActivateSubscriptionSuccess,
	props<{
		subscriptionId: number;
		inViewMode: boolean;
	}>()
);

export const activateBulkSubscriptionSuccess = createAction(
	SubscriptionActions.ActivateBulkSubscriptionSuccess
);

export const activateSubscriptionFail = createAction(
	SubscriptionActions.ActivateSubscriptionFail,
	props<{ error: unknown }>()
);

export const deactivateSubscription = createAction(
	SubscriptionActions.DeactivateSubscription,
	props<{
		subscriptionId: number;
		inViewMode: boolean;
	}>()
);

export const deactivateBulkSubscription = createAction(
	SubscriptionActions.DeactivateBulkSubscription,
	props<{ subscriptionIds: Array<number> }>()
);

export const deactivateSubscriptionSuccess = createAction(
	SubscriptionActions.DeactivateSubscriptionSuccess,
	props<{
		subscriptionId: number;
		inViewMode: boolean;
	}>()
);

export const deactivateBulkSubscriptionSuccess = createAction(
	SubscriptionActions.DeactivateBulkSubscriptionSuccess
);

export const deactivateSubscriptionFail = createAction(
	SubscriptionActions.DeactivateSubscriptionFail,
	props<{ error: unknown }>()
);

export const updateSubscriptionData = createAction(
	SubscriptionActions.UpdateSubscriptionData,
	props<{ subscriptionId: number; operationType: OperationType }>()
);

export const updateSubscriptionDataOnList = createAction(
	SubscriptionActions.UpdateSubscriptionDataOnList,
	props<{ subscriptionId: number; operationType: OperationType }>()
);

export const updateSubscriptionDataSuccess = createAction(
	SubscriptionActions.UpdateSubscriptionDataSuccess
);

export const updateSubscriptionDataFail = createAction(
	SubscriptionActions.UpdateSubscriptionDataFail,
	props<{ error: unknown }>()
);

export const clearSubscriptionData = createAction(
	SubscriptionActions.ClearSubscriptionData,
	props<{
		subscriptionId: number;
		inViewMode: boolean;
	}>()
);

export const clearBulkSubscriptionData = createAction(
	SubscriptionActions.ClearBulkSubscriptionData,
	props<{ subscriptionIds: Array<number> }>()
);

export const clearSubscriptionDataSuccess = createAction(
	SubscriptionActions.ClearSubscriptionDataSuccess,
	props<{
		subscriptionId: number;
		inViewMode: boolean;
	}>()
);

export const clearBulkSubscriptionDataSuccess = createAction(
	SubscriptionActions.ClearBulkSubscriptionDataSuccess
);

export const clearSubscriptionDataFail = createAction(
	SubscriptionActions.ClearSubscriptionDataFail,
	props<{ error: unknown }>()
);

export const deleteSubscription = createAction(
	SubscriptionActions.DeleteSubscription,
	props<{ subscriptionId: number }>()
);

export const deleteBulkSubscription = createAction(
	SubscriptionActions.DeleteBulkSubscription,
	props<{ subscriptionIds: Array<number> }>()
);

export const deleteSubscriptionSuccess = createAction(
	SubscriptionActions.DeleteSubscriptionSuccess
);

export const deleteSubscriptionFail = createAction(
	SubscriptionActions.DeleteSubscriptionFail,
	props<{ error: unknown }>()
);

export const updateProfile = createAction(
	SubscriptionActions.UpdateProfile,
	props<{
		profile: ProfileItemEntity;
		columnModel: ColumnModelItemEntity | string;
		filterModels: Array<FilterModelItemEntity | string>;
	}>()
);

export const updateProfileSuccess = createAction(
	SubscriptionActions.UpdateProfileSuccess
);

export const updateProfileFail = createAction(
	SubscriptionActions.UpdateProfileFail,
	props<{ error: unknown }>()
);

export const deleteProfile = createAction(
	SubscriptionActions.DeleteProfile,
	props<{ profileId: string }>()
);

export const deleteBulkProfile = createAction(
	SubscriptionActions.DeleteBulkProfile,
	props<{ profileIds: Array<string> }>()
);

export const deleteProfileSuccess = createAction(
	SubscriptionActions.DeleteProfileSuccess
);

export const deleteProfileFail = createAction(
	SubscriptionActions.DeleteProfileFail,
	props<{ error: unknown }>()
);

export const updateColumnModel = createAction(
	SubscriptionActions.UpdateColumnModel,
	props<{ columnModel: ColumnModelItemEntity }>()
);

export const updateColumnModelSuccess = createAction(
	SubscriptionActions.UpdateColumnModelSuccess
);

export const updateColumnModelFail = createAction(
	SubscriptionActions.UpdateColumnModelFail,
	props<{ error: unknown }>()
);

export const deleteColumnModel = createAction(
	SubscriptionActions.DeleteColumnModel,
	props<{ columnModelId: string }>()
);

export const deleteBulkColumnModel = createAction(
	SubscriptionActions.DeleteBulkColumnModel,
	props<{ columnModelIds: Array<string> }>()
);

export const deleteColumnModelSuccess = createAction(
	SubscriptionActions.DeleteColumnModelSuccess
);

export const deleteColumnModelFail = createAction(
	SubscriptionActions.DeleteColumnModelFail,
	props<{ error: unknown }>()
);

export const updateFilterModel = createAction(
	SubscriptionActions.UpdateFilterModel,
	props<{ filterModel: FilterModelItemEntity }>()
);

export const updateFilterModelSuccess = createAction(
	SubscriptionActions.UpdateFilterModelSuccess
);

export const updateFilterModelFail = createAction(
	SubscriptionActions.UpdateFilterModelFail,
	props<{ error: unknown }>()
);

export const deleteFilterModel = createAction(
	SubscriptionActions.DeleteFilterModel,
	props<{ filterModelId: string }>()
);

export const deleteBulkFilterModel = createAction(
	SubscriptionActions.DeleteBulkFilterModel,
	props<{ filterModelIds: Array<string> }>()
);

export const deleteFilterModelSuccess = createAction(
	SubscriptionActions.DeleteFilterModelSuccess
);

export const deleteFilterModelFail = createAction(
	SubscriptionActions.DeleteFilterModelFail,
	props<{ error: unknown }>()
);

export const initPermissionsConfig = createAction(
	SubscriptionActions.InitPermissionsConfig
);

export const refreshMembersAccess = createAction(
	SubscriptionActions.RefreshMembersAccess
);

export const fetchMembersAccessSuccess = createAction(
	SubscriptionActions.FetchMembersAccessSuccess,
	props<{ membersAccess: Map<string, MemberAccessEntity> }>()
);

export const fetchMembersAccessFail = createAction(
	SubscriptionActions.FetchMembersAccessFail,
	props<{ error: unknown }>()
);

export const updateMembersAccess = createAction(
	SubscriptionActions.UpdateMembersAccess,
	props<{ membersAccess: Array<MemberAccessEntity> }>()
);

export const updateMembersAccessSuccess = createAction(
	SubscriptionActions.UpdateMemberAccessSuccess
);

export const updateMembersAccessFail = createAction(
	SubscriptionActions.UpdateMemberAccessFail,
	props<{ error: unknown }>()
);

export const getSwitchableConsumedProjectsForSubscription = createAction(
	SubscriptionActions.GetSwitchableConsumedProjectsForSubscription
);

export const refreshSwitchableConsumedProjectsForSubscription = createAction(
	SubscriptionActions.RefreshSwitchableConsumedProjectsForSubscription
);

export const getSwitchableConsumedProjects = createAction(
	SubscriptionActions.GetSwitchableConsumedProjects
);

export const refreshSwitchableConsumedProjects = createAction(
	SubscriptionActions.RefreshSwitchableConsumedProjects
);

export const fetchSwitchableConsumedProjectsSuccess = createAction(
	SubscriptionActions.FetchSwitchableConsumedProjectsSuccess,
	props<{ projects: Map<string, SwitchableVersionProjectItemEntity> }>()
);

export const fetchSwitchableConsumedProjectsFail = createAction(
	SubscriptionActions.FetchSwitchableConsumedProjectsFail,
	props<{ error: unknown }>()
);

export const switchVersionForConsumedProjectsOnView = createAction(
	SubscriptionActions.SwitchVersionForConsumedProjectsOnView,
	props<{ projectIds: Array<number> }>()
);

export const switchVersionForConsumedProjectsOnViewSuccess = createAction(
	SubscriptionActions.SwitchVersionForConsumedProjectsOnViewSuccess
);

export const switchVersionForConsumedProjectsOnViewFail = createAction(
	SubscriptionActions.SwitchVersionForConsumedProjectsOnViewFail,
	props<{ error: unknown }>()
);

export const switchVersionForConsumedProjects = createAction(
	SubscriptionActions.SwitchVersionForConsumedProjects,
	props<{ projectIds: Array<number> }>()
);

export const switchVersionForConsumedProjectsSuccess = createAction(
	SubscriptionActions.SwitchVersionForConsumedProjectsSuccess
);

export const switchVersionForConsumedProjectsFail = createAction(
	SubscriptionActions.SwitchVersionForConsumedProjectsFail,
	props<{ error: unknown }>()
);
